import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import Button from '../../components/base/Button';
import Input from '../../components/base/Input';
import Select from '../../components/base/Select';
import Modal from '../../components/base/Modal';

interface Product {
  id: string;
  name: string;
  category: string;
  purchasePrice: number;
  sellPrice: number;
  location: string;
  divisible: boolean;
  minStock: number;
  currentStock: number;
  supplier: string;
  expiryDate: string;
  automatPrices: { [automatId: string]: number };
}

interface Notification {
  id: number;
  type: 'low-stock' | 'expiring';
  productName: string;
  message: string;
  timestamp: Date;
}

export default function Lager() {
  const { user } = useAuth();
  const [products, setProducts] = useState<Product[]>([]);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [automaten, setAutomaten] = useState<{ id: string; name: string }[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [showMoreMenu, setShowMoreMenu] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [filterCategory, setFilterCategory] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [importing, setImporting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    category: '',
    purchasePrice: '',
    sellPrice: '',
    location: '',
    divisible: false,
    minStock: '',
    currentStock: '',
    supplier: '',
    expiryDate: '',
    automatPrices: {} as { [key: string]: string }
  });

  const categories = [
    'Süsswaren',
    'Gesunde Snacks', 
    'Getränke',
    'Tabak',
    'Andere',
    'Snacks'
  ];

  // Load products and automaten from Supabase
  useEffect(() => {
    if (user) {
      loadProducts();
      loadAutomaten();
    }
  }, [user]);

  const loadProducts = async () => {
    try {
      const { data, error } = await supabase
        .from('daten')
        .select('*')
        .eq('user_id', user?.id)
        .eq('data_type', 'product')
        .order('created_at', { ascending: false });

      if (error) throw error;

      const productData = data.map(item => ({
        id: item.id,
        ...item.data
      }));

      setProducts(productData);
    } catch (error) {
      console.error('Error loading products:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadAutomaten = async () => {
    try {
      const { data, error } = await supabase
        .from('daten')
        .select('*')
        .eq('user_id', user?.id)
        .eq('data_type', 'automat'); // KORRIGIERT: Verwendet wieder 'automat'

      if (error) throw error;

      console.log('🔍 Loaded automat data for Lager:', data.length, 'items');

      // ERWEITERT: Filtere nur echte Automaten-Maschinen
      const automatenData = data
        .filter(item => {
          // Nur echte Automaten-Maschinen für Lager verwenden
          return item.data && item.data.machine_type === 'physical_automat';
        })
        .map(item => ({
          id: item.id,
          name: item.data.name
        }));

      console.log('🤖 Final automaten for Lager (physical machines):', automatenData.length);
      console.log('🤖 Automaten names for Lager:', automatenData.map(a => a.name));

      setAutomaten(automatenData);
    } catch (error) {
      console.error('Error loading automaten:', error);
    }
  };

  // Prüfung auf niedrige Bestände und bald ablaufende Produkte
  useEffect(() => {
    const checkNotifications = () => {
      const newNotifications: Notification[] = [];
      const today = new Date();
      const fourWeeksFromNow = new Date(today.getTime() + 28 * 24 * 60 * 60 * 1000);

      products.forEach(product => {
        // Prüfung auf niedrigen Bestand - verbesserte Meldung
        if (product.currentStock <= product.minStock) {
          newNotifications.push({
            id: Date.now() + Math.random(),
            type: 'low-stock',
            productName: product.name,
            message: `Mindestbestand erreicht! Aktuell ${product.currentStock} Stück, sollten mindestens ${product.minStock} Stück sein. Bitte nachbestellen.`,
            timestamp: new Date()
          });
        }

        // Prüfung auf bald ablaufende Produkte - erweitert auf 28 Tage
        if (product.expiryDate) {
          const expiryDate = new Date(product.expiryDate);
          if (expiryDate <= fourWeeksFromNow && expiryDate > today) {
            const daysUntilExpiry = Math.ceil((expiryDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
            newNotifications.push({
              id: Date.now() + Math.random(),
              type: 'expiring',
              productName: product.name,
              message: `Läuft in ${daysUntilExpiry} Tag${daysUntilExpiry === 1 ? '' : 'en'} ab! Ablaufdatum: ${new Date(product.expiryDate).toLocaleDateString('de-DE')}`,
              timestamp: new Date()
            });
          }
        }
      });

      setNotifications(newNotifications);
    };

    checkNotifications();
  }, [products]);

  const handleInputChange = (field: string, value: string | boolean) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleAutomatPriceChange = (automatId: string, price: string) => {
    setFormData(prev => ({
      ...prev,
      automatPrices: {
        ...prev.automatPrices,
        [automatId]: price
      }
    }));
  };

  // CSV Import Handler
  const handleCSVImport = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setImporting(true);

    try {
      const text = await file.text();
      console.log('📄 CSV Datei gelesen, Inhalt:', text.substring(0, 200) + '...');
      
      // Bereinige den Text von BOM und anderen unsichtbaren Zeichen
      const cleanText = text.replace(/^\uFEFF/, '').replace(/\r\n/g, '\n').replace(/\r/g, '\n');
      
      const lines = cleanText.split('\n').filter(line => line.trim());
      
      if (lines.length === 0) {
        alert('CSV-Datei ist leer.');
        setImporting(false);
        return;
      }

      console.log('📊 Anzahl Zeilen gefunden:', lines.length);
      console.log('🔍 Erste 3 Zeilen:', lines.slice(0, 3));

      // Erweiterte Trennzeichen-Erkennung
      const firstLine = lines[0];
      let separator = '\t'; // Standard: Tab
      
      // Prüfe verschiedene Trennzeichen
      const tabCount = (firstLine.match(/\t/g) || []).length;
      const commaCount = (firstLine.match(/,/g) || []).length;
      const semicolonCount = (firstLine.match(/;/g) || []).length;
      
      console.log('🔍 Trennzeichen-Analyse:', { tabCount, commaCount, semicolonCount });
      
      if (tabCount >= 7) {
        separator = '\t';
        console.log('✅ Tab-Trennzeichen erkannt');
      } else if (semicolonCount >= 7) {
        separator = ';';
        console.log('✅ Semikolon-Trennzeichen erkannt');
      } else if (commaCount >= 7) {
        separator = ',';
        console.log('✅ Komma-Trennzeichen erkannt');
      } else {
        // Fallback: Versuche alle Trennzeichen
        console.log('⚠️ Unklares Trennzeichen, versuche Tab als Standard');
        separator = '\t';
      }

      // Parse header - Prüfe ob erste Zeile Header ist
      let dataStartIndex = 0;
      let hasHeader = false;
      
      const firstLineValues = firstLine.split(separator).map(h => h.trim().replace(/"/g, ''));
      console.log('📋 Erste Zeile Werte (Anzahl: ' + firstLineValues.length + '):', firstLineValues);
      
      // Erweiterte Header-Erkennung
      if (firstLineValues.length >= 4 && (
        firstLineValues[0].toLowerCase().includes('name') ||
        firstLineValues.some(val => val.toLowerCase().includes('kategorie')) ||
        firstLineValues.some(val => val.toLowerCase().includes('preis'))
      )) {
        hasHeader = true;
        dataStartIndex = 1;
        console.log('✅ Header erkannt, starte bei Zeile 2');
      } else {
        console.log('📝 Keine Header erkannt, starte bei Zeile 1');
      }

      const importedProducts = [];
      let successCount = 0;
      let errorCount = 0;
      const errors: string[] = [];

      // Parse data rows
      for (let i = dataStartIndex; i < lines.length; i++) {
        try {
          const line = lines[i].trim();
          if (!line) {
            console.log(`⏭️ Zeile ${i + 1}: Leer, übersprungen`);
            continue;
          }

          // Split und bereinige Werte - erweiterte Bereinigung
          const values = line.split(separator).map(v => {
            // Entferne Anführungszeichen und trimme
            let cleaned = v.trim().replace(/^["']|["']$/g, '');
            return cleaned;
          });
          
          console.log(`📝 Zeile ${i + 1} Werte (Anzahl: ${values.length}):`, values);
          
          // Mindestens 4 Spalten erforderlich (Name, Kategorie, Einkaufspreis, Verkaufspreis)
          if (values.length < 4) {
            const error = `Zeile ${i + 1}: Nicht genügend Spalten (${values.length}/mindestens 4). Gefundene Werte: [${values.join(', ')}]. Verwendetes Trennzeichen: ${separator === '\t' ? 'Tab' : separator}`;
            console.warn(error);
            errors.push(error);
            errorCount++;
            continue;
          }

          // Flexible Zuordnung basierend auf Anzahl der Spalten
          let name, stueck, ablaufdatum, kategorie, mindestbestand, einkaufspreis, verkaufspreis, lager;

          if (values.length >= 8) {
            // Vollständiges Format: Name, Stück, Ablaufdatum, Kategorie, Mindestbestand, Einkaufspreis, Verkaufspreis, Lager
            [name, stueck, ablaufdatum, kategorie, mindestbestand, einkaufspreis, verkaufspreis, lager] = values;
          } else if (values.length >= 6) {
            // Reduziertes Format: Name, Kategorie, Mindestbestand, Einkaufspreis, Verkaufspreis, Lager
            [name, kategorie, mindestbestand, einkaufspreis, verkaufspreis, lager] = values;
            stueck = '0';
            ablaufdatum = '';
          } else if (values.length >= 4) {
            // Minimales Format: Name, Kategorie, Einkaufspreis, Verkaufspreis
            [name, kategorie, einkaufspreis, verkaufspreis] = values;
            stueck = '0';
            ablaufdatum = '';
            mindestbestand = '10';
            lager = 'Hauptlager';
          }

          // Validate required fields
          if (!name || !kategorie || !einkaufspreis || !verkaufspreis) {
            const error = `Zeile ${i + 1}: Fehlende Pflichtfelder (Name: "${name}", Kategorie: "${kategorie}", Einkaufspreis: "${einkaufspreis}", Verkaufspreis: "${verkaufspreis}")`;
            console.warn(error);
            errors.push(error);
            errorCount++;
            continue;
          }

          // Validiere und konvertiere Preise - erweiterte Bereinigung
          const cleanEinkaufspreis = einkaufspreis.toString().replace(/,/g, '.').replace(/[^\d.-]/g, '').trim();
          const cleanVerkaufspreis = verkaufspreis.toString().replace(/,/g, '.').replace(/[^\d.-]/g, '').trim();
          
          const parsedEinkaufspreis = parseFloat(cleanEinkaufspreis);
          const parsedVerkaufspreis = parseFloat(cleanVerkaufspreis);

          if (isNaN(parsedEinkaufspreis) || isNaN(parsedVerkaufspreis) || parsedEinkaufspreis < 0 || parsedVerkaufspreis < 0) {
            const error = `Zeile ${i + 1}: Ungültige Preise (Einkauf: "${einkaufspreis}" → "${cleanEinkaufspreis}", Verkauf: "${verkaufspreis}" → "${cleanVerkaufspreis}")`;
            console.warn(error);
            errors.push(error);
            errorCount++;
            continue;
          }

          // Validiere Kategorie - case-insensitive
          const normalizedKategorie = kategorie.trim();
          const validCategory = categories.find(cat => 
            cat.toLowerCase() === normalizedKategorie.toLowerCase()
          );
          
          if (!validCategory) {
            const error = `Zeile ${i + 1}: Unbekannte Kategorie "${kategorie}". Verfügbare Kategorien: ${categories.join(', ')}`;
            console.warn(error);
            errors.push(error);
            errorCount++;
            continue;
          }

          const productData = {
            name: name.trim(),
            category: validCategory,
            purchasePrice: parsedEinkaufspreis,
            sellPrice: parsedVerkaufspreis,
            location: (lager || 'Hauptlager').trim(),
            divisible: false,
            minStock: parseInt(mindestbestand?.toString().replace(/[^\d]/g, '') || '10') || 10,
            currentStock: parseInt(stueck?.toString().replace(/[^\d]/g, '') || '0') || 0,
            supplier: 'CSV Import',
            expiryDate: ablaufdatum?.trim() || '',
            automatPrices: {}
          };

          // Datum-Format konvertieren (von DD.MM.YYYY zu YYYY-MM-DD)
          if (productData.expiryDate && productData.expiryDate.includes('.')) {
            const dateParts = productData.expiryDate.split('.');
            if (dateParts.length === 3) {
              const [day, month, year] = dateParts;
              if (day && month && year && year.length === 4) {
                productData.expiryDate = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
              } else {
                productData.expiryDate = '';
              }
            }
          }

          console.log(`✅ Zeile ${i + 1} verarbeitet:`, productData);

          // Insert into Supabase
          const { data, error } = await supabase
            .from('daten')
            .insert({
              user_id: user?.id,
              data_type: 'product',
              data: productData
            })
            .select()
            .single();

          if (error) {
            const errorMsg = `Zeile ${i + 1}: Datenbankfehler - ${error.message}`;
            console.error(errorMsg, error);
            errors.push(errorMsg);
            errorCount++;
            continue;
          }

          importedProducts.push({
            id: data.id,
            ...productData
          });
          successCount++;

        } catch (error) {
          const errorMsg = `Zeile ${i + 1}: Unerwarteter Fehler - ${error instanceof Error ? error.message : 'Unbekannter Fehler'}`;
          console.error(errorMsg, error);
          errors.push(errorMsg);
          errorCount++;
        }
      }

      // Update products list
      if (importedProducts.length > 0) {
        setProducts(prev => [...importedProducts, ...prev]);
      }

      // Show detailed result
      let resultMessage = `CSV-Import abgeschlossen!\n\n✅ Erfolgreich importiert: ${successCount}\n❌ Fehler: ${errorCount}`;
      
      if (successCount > 0) {
        resultMessage += `\n\n📋 Beispiel erfolgreich importiert:\n${importedProducts[0]?.name} → ${importedProducts[0]?.currentStock} Stück → ${importedProducts[0]?.category} → CHF ${importedProducts[0]?.purchasePrice?.toFixed(2)}/${importedProducts[0]?.sellPrice?.toFixed(2)}`;
      }

      if (errorCount > 0 && errors.length > 0) {
        resultMessage += `\n\n⚠️ Erste 3 Fehler:\n${errors.slice(0, 3).join('\n')}`;
        if (errors.length > 3) {
          resultMessage += `\n... und ${errors.length - 3} weitere Fehler`;
        }
        
        // Zusätzliche Hilfe bei Trennzeichen-Problemen
        if (errors.some(e => e.includes('Nicht genügend Spalten'))) {
          resultMessage += `\n\n💡 Tipp: Stellen Sie sicher, dass Ihre CSV-Datei Tab-getrennte Werte verwendet.\nErkanntes Trennzeichen: ${separator === '\t' ? 'Tab' : separator}\nErwartetes Format: Name[TAB]Stück[TAB]Ablaufdatum[TAB]Kategorie[TAB]Mindestbestand[TAB]Einkaufspreis[TAB]Verkaufspreis[TAB]Lager`;
        }
      }

      alert(resultMessage);

    } catch (error) {
      console.error('CSV Import Hauptfehler:', error);
      alert(`Fehler beim Importieren der CSV-Datei:\n${error instanceof Error ? error.message : 'Unbekannter Fehler'}\n\nBitte überprüfen Sie das Dateiformat und stellen Sie sicher, dass es Tab-getrennte Werte enthält.`);
    } finally {
      setImporting(false);
      // Reset file input
      e.target.value = '';
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const productData = {
      name: formData.name,
      category: formData.category,
      purchasePrice: parseFloat(formData.purchasePrice),
      sellPrice: parseFloat(formData.sellPrice),
      location: formData.location,
      divisible: formData.divisible,
      minStock: parseInt(formData.minStock),
      currentStock: parseInt(formData.currentStock),
      supplier: formData.supplier,
      expiryDate: formData.expiryDate,
      automatPrices: Object.fromEntries(
        Object.entries(formData.automatPrices).map(([key, value]) => [key, parseFloat(value)])
      )
    };

    try {
      const { data, error } = await supabase
        .from('daten')
        .insert({
          user_id: user?.id,
          data_type: 'product',
          data: productData
        })
        .select()
        .single();

      if (error) throw error;

      setProducts(prev => [{
        id: data.id,
        ...productData
      }, ...prev]);

      setIsModalOpen(false);
      resetForm();
      alert('Produkt erfolgreich hinzugefügt!');
    } catch (error) {
      console.error('Error adding product:', error);
      alert('Fehler beim Hinzufügen des Produkts.');
    }
  };

  const handleEdit = (product: Product) => {
    setEditingProduct(product);
    setFormData({
      name: product.name,
      category: product.category,
      purchasePrice: product.purchasePrice.toString(),
      sellPrice: product.sellPrice.toString(),
      location: product.location,
      divisible: product.divisible,
      minStock: product.minStock.toString(),
      currentStock: product.currentStock.toString(),
      supplier: product.supplier,
      expiryDate: product.expiryDate,
      automatPrices: Object.fromEntries(
        Object.entries(product.automatPrices || {}).map(([key, value]) => [key, value.toString()])
      )
    });
    setIsEditModalOpen(true);
    setShowMoreMenu(null);
  };

  // Funktion um den effektiven Verkaufspreis für einen Automaten zu bekommen
  const getEffectivePrice = (product: Product, automatId?: string) => {
    if (automatId && product.automatPrices && product.automatPrices[automatId]) {
      return product.automatPrices[automatId];
    }
    return product.sellPrice;
  };

  // Funktion um zu prüfen ob ein Produkt spezifische Preise hat
  const hasSpecificPrices = (product: Product) => {
    return product.automatPrices && Object.keys(product.automatPrices).length > 0;
  };

  const handleEditSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!editingProduct) return;

    const productData = {
      name: formData.name,
      category: formData.category,
      purchasePrice: parseFloat(formData.purchasePrice),
      sellPrice: parseFloat(formData.sellPrice),
      location: formData.location,
      divisible: formData.divisible,
      minStock: parseInt(formData.minStock),
      currentStock: parseInt(formData.currentStock),
      supplier: formData.supplier,
      expiryDate: formData.expiryDate,
      automatPrices: Object.fromEntries(
        Object.entries(formData.automatPrices).map(([key, value]) => [key, parseFloat(value)])
      )
    };

    try {
      const { error } = await supabase
        .from('daten')
        .update({ data: productData })
        .eq('id', editingProduct.id);

      if (error) throw error;

      setProducts(prev => prev.map(p => 
        p.id === editingProduct.id ? { ...p, ...productData } : p
      ));

      setIsEditModalOpen(false);
      setEditingProduct(null);
      resetForm();
      alert('Produkt erfolgreich aktualisiert!');
    } catch (error) {
      console.error('Error updating product:', error);
      alert('Fehler beim Aktualisieren des Produkts.');
    }
  };

  const handleDelete = async (product: Product) => {
    if (!confirm(`Sind Sie sicher, dass Sie "${product.name}" löschen möchten?`)) {
      return;
    }

    try {
      const { error } = await supabase
        .from('daten')
        .delete()
        .eq('id', product.id);

      if (error) throw error;

      setProducts(prev => prev.filter(p => p.id !== product.id));
      setShowMoreMenu(null);
      alert('Produkt erfolgreich gelöscht!');
    } catch (error) {
      console.error('Error deleting product:', error);
      alert('Fehler beim Löschen des Produkts.');
    }
  };

  const handleDuplicate = async (product: Product) => {
    const duplicateData = {
      name: `${product.name} (Kopie)`,
      category: product.category,
      purchasePrice: product.purchasePrice,
      sellPrice: product.sellPrice,
      location: product.location,
      divisible: product.divisible,
      minStock: product.minStock,
      currentStock: 0,
      supplier: product.supplier,
      expiryDate: '',
      automatPrices: product.automatPrices || {}
    };

    try {
      const { data, error } = await supabase
        .from('daten')
        .insert({
          user_id: user?.id,
          data_type: 'product',
          data: duplicateData
        })
        .select()
        .single();

      if (error) throw error;

      setProducts(prev => [{
        id: data.id,
        ...duplicateData
      }, ...prev]);

      setShowMoreMenu(null);
      alert('Produkt erfolgreich dupliziert!');
    } catch (error) {
      console.error('Error duplicating product:', error);
      alert('Fehler beim Duplizieren des Produkts.');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      category: '',
      purchasePrice: '',
      sellPrice: '',
      location: '',
      divisible: false,
      minStock: '',
      currentStock: '',
      supplier: '',
      expiryDate: '',
      automatPrices: {}
    });
  };

  const filteredProducts = products.filter(product => {
    const matchesCategory = !filterCategory || product.category === filterCategory;
    const matchesSearch = !searchTerm || 
      product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      product.supplier.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const getStockStatus = (product: Product) => {
    if (product.currentStock <= product.minStock) {
      return { status: 'critical', color: 'bg-red-100 text-red-800', icon: 'ri-error-warning-line' };
    } else if (product.currentStock <= product.minStock * 1.5) {
      return { status: 'warning', color: 'bg-yellow-100 text-yellow-800', icon: 'ri-alert-line' };
    }
    return { status: 'good', color: 'bg-green-100 text-green-800', icon: 'ri-check-line' };
  };

  // KORREKTUR: Die ursprüngliche Berechnung war falsch - sie verwendete Millisekunden statt Tage
  const getExpiryStatus = (expiryDate: string) => {
    if (!expiryDate) return null;
    
    const today = new Date();
    const expiry = new Date(expiryDate);
    const daysUntilExpiry = Math.ceil((expiry.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
    
    if (daysUntilExpiry <= 0) {
      return { status: 'expired', color: 'bg-red-100 text-red-800', text: 'Abgelaufen' };
    } else if (daysUntilExpiry <= 28) {
      return { status: 'expiring', color: 'bg-orange-100 text-orange-800', text: `${daysUntilExpiry} Tag${daysUntilExpiry === 1 ? '' : 'e'}` };
    }
    return null;
  };

  // Schließe Mehr-Menü wenn außerhalb geklickt wird
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (!event.target || !(event.target as Element).closest('.more-menu-container')) {
        setShowMoreMenu(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleDeleteAll = async () => {
    if (!confirm('Sind Sie sicher, dass Sie ALLE Produkte aus dem Lager löschen möchten? Diese Aktion kann nicht rückgängig gemacht werden!')) {
      return;
    }

    // Zweite Bestätigung für zusätzliche Sicherheit
    if (!confirm('LETZTE WARNUNG: Alle Produkte werden unwiderruflich gelöscht. Möchten Sie wirklich fortfahren?')) {
      return;
    }

    try {
      setLoading(true);
      
      // Lösche alle Produkte des Benutzers
      const { error } = await supabase
        .from('daten')
        .delete()
        .eq('user_id', user?.id)
        .eq('data_type', 'product');

      if (error) throw error;

      // Lokalen State leeren
      setProducts([]);
      setNotifications([]);
      
      alert('✅ Alle Produkte wurden erfolgreich aus dem Lager entfernt!');
    } catch (error) {
      console.error('Error deleting all products:', error);
      alert('❌ Fehler beim Löschen der Produkte. Bitte versuchen Sie es erneut.');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateAllSuppliers = async () => {
    if (!confirm('Möchten Sie bei ALLEN Produkten den Lieferanten auf "PowerForce" setzen?')) {
      return;
    }

    try {
      setLoading(true);
      
      // Aktualisiere alle Produkte des Benutzers
      const updatedProducts = products.map(product => ({
        ...product,
        supplier: 'PowerForce'
      }));

      // Update in Supabase
      for (const product of updatedProducts) {
        const { error } = await supabase
          .from('daten')
          .update({ 
            data: {
              ...product,
              supplier: 'PowerForce'
            }
          })
          .eq('id', product.id);

        if (error) {
          console.error('Error updating product:', product.name, error);
        }
      }

      // Lokalen State aktualisieren
      setProducts(updatedProducts);
      
      alert(`✅ Alle ${products.length} Produkte haben jetzt "PowerForce" als Lieferanten!`);
    } catch (error) {
      console.error('Error updating suppliers:', error);
      alert('❌ Fehler beim Aktualisieren der Lieferanten. Bitte versuchen Sie es erneut.');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen w-full flex items-center justify-center">
        <div className="text-center">
          <i className="ri-loader-line text-4xl text-blue-600 animate-spin mb-4"></i>
          <p className="text-gray-600">Lade Lager-Daten...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen w-full overflow-x-hidden">
      <div className="w-full max-w-full overflow-x-hidden">
        {/* Benachrichtigungen */}
        {notifications.length > 0 && (
          <div className="mb-3 sm:mb-4 space-y-2 px-2 sm:px-0">
            {notifications.map((notification) => (
              <div
                key={notification.id}
                className={`p-2 sm:p-3 rounded-lg border-l-4 ${
                  notification.type === 'low-stock' 
                    ? 'bg-red-50 border-red-400' 
                    : 'bg-orange-50 border-orange-400'
                }`}
              >
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <i className={`${notification.type === 'low-stock' 
                      ? 'ri-error-warning-line text-red-400' 
                      : 'ri-time-line text-orange-400'
                      } w-4 h-4 sm:w-5 sm:h-5 flex items-center justify-center`}></i>
                  </div>
                  <div className="ml-2 sm:ml-3">
                    <h3 className={`text-xs sm:text-sm font-medium ${
                      notification.type === 'low-stock' ? 'text-red-800' : 'text-orange-800'
                    }`}>
                      {notification.productName}
                    </h3>
                    <p className={`text-xs sm:text-sm ${
                      notification.type === 'low-stock' ? 'text-red-700' : 'text-orange-700'
                    }`}>
                      {notification.message}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="bg-white rounded-lg shadow-sm border w-full overflow-x-hidden">
          <div className="px-2 sm:px-4 lg:px-6 py-3 sm:py-4 border-b border-gray-200">
            <div className="flex flex-col space-y-2 sm:space-y-0 sm:flex-row sm:justify-between sm:items-center">
              <div className="min-w-0 flex-1">
                <h1 className="text-base sm:text-xl lg:text-2xl font-bold text-gray-900 flex items-center">
                  Lager-Verwaltung
                  <span className="ml-2 sm:ml-3 text-xs sm:text-sm text-blue-600">
                    <i className="ri-cloud-line mr-1"></i>
                    Supabase
                  </span>
                </h1>
                <p className="text-gray-600 mt-1 text-xs sm:text-base break-words">
                  Verwalten Sie Ihren Produktbestand ({products.length} Produkte)
                </p>
              </div>
              <div className="flex items-center space-x-2">
                {/* CSV Import Button - Klein und diskret in Grün */}
                <label 
                  className="relative bg-green-600 text-white px-2 py-1.5 rounded-md hover:bg-green-700 transition-colors cursor-pointer whitespace-nowrap flex items-center text-xs"
                  title="CSV-Datei importieren"
                >
                  <input
                    type="file"
                    accept=".csv"
                    onChange={handleCSVImport}
                    className="hidden"
                    disabled={importing}
                  />
                  {importing ? (
                    <>
                      <i className="ri-loader-line animate-spin w-3 h-3 flex items-center justify-center mr-1"></i>
                      <span className="hidden sm:inline">Import...</span>
                    </>
                  ) : (
                    <>
                      <i className="ri-file-excel-line w-3 h-3 flex items-center justify-center mr-1"></i>
                      <span className="hidden sm:inline">CSV Import</span>
                      <span className="sm:hidden">CSV</span>
                    </>
                  )}
                </label>

                {/* Alle Produkte löschen Button */}
                {products.length > 0 && (
                  <button
                    onClick={handleDeleteAll}
                    className="bg-red-600 text-white px-2 py-1.5 rounded-md hover:bg-red-700 transition-colors cursor-pointer whitespace-nowrap flex items-center text-xs"
                    title="Alle Produkte löschen"
                  >
                    <i className="ri-delete-bin-line w-3 h-3 flex items-center justify-center mr-1"></i>
                    <span className="hidden sm:inline">Alle löschen</span>
                    <span className="sm:hidden">Löschen</span>
                  </button>
                )}

                {/* PowerForce Lieferant Button */}
                {products.length > 0 && (
                  <button
                    onClick={handleUpdateAllSuppliers}
                    className="bg-green-600 text-white px-2 py-1.5 rounded-md hover:bg-green-700 transition-colors cursor-pointer whitespace-nowrap flex items-center text-xs"
                    title="Alle Lieferanten auf PowerForce setzen"
                  >
                    <i className="ri-truck-line w-3 h-3 flex items-center justify-center mr-1"></i>
                    <span className="hidden sm:inline">PowerForce</span>
                    <span className="sm:hidden">PF</span>
                  </button>
                )}

                <button
                  onClick={() => setIsModalOpen(true)}
                  className="w-full sm:w-auto bg-blue-600 text-white px-3 sm:px-4 py-2 rounded-md hover:bg-blue-700 transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center flex-shrink-0 text-xs sm:text-sm"
                >
                  <i className="ri-add-line w-3 h-3 sm:w-4 sm:h-4 flex items-center justify-center mr-1 sm:mr-2"></i>
                  Produkt hinzufügen
                </button>
              </div>
            </div>

            {/* CSV Format Hinweis - KORRIGIERT: Ihr exaktes Format */}
            <div className="mt-3 p-2 bg-green-50 border border-green-200 rounded-md">
              <div className="flex items-start">
                <i className="ri-information-line text-green-600 mr-2 mt-0.5 flex-shrink-0"></i>
                <div className="text-xs text-green-800">
                  <strong>CSV-Format (Tab-getrennt):</strong> Name → Stück → Ablaufdatum → Kategorie → Mindestbestand → Einkaufspreis → Verkaufspreis → Lager
                  <br />
                  <strong>Beispiel:</strong> BIFLAME Sturmfeuerzeug	50	01.03.2026	Tabak	25	0.5	2.7	Artan
                  <br />
                  <strong>Pflichtfelder:</strong> Name, Kategorie, Einkaufspreis, Verkaufspreis
                </div>
              </div>
            </div>
          </div>

          {/* Filter und Suche */}
          {products.length > 0 && (
            <div className="px-2 sm:px-4 lg:px-6 py-3 sm:py-4 border-b border-gray-200 bg-gray-50">
              <div className="flex flex-col sm:flex-row gap-2 sm:gap-4">
                <div className="flex-1">
                  <Input
                    placeholder="Produkte durchsuchen..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full text-xs sm:text-sm"
                  />
                </div>
                <div className="sm:w-40 lg:w-48">
                  <Select
                    options={[
                      { value: '', label: 'Alle Kategorien' },
                      ...categories.map(cat => ({ value: cat, label: cat }))
                    ]}
                    value={filterCategory}
                    onChange={(value) => setFilterCategory(value)}
                  />
                </div>
              </div>
            </div>
          )}

          {/* Produktliste */}
          <div className="p-2 sm:p-4 lg:p-6 w-full overflow-x-hidden">
            {products.length === 0 ? (
              <div className="text-center py-8 sm:py-12">
                <div className="text-gray-400 mb-3 sm:mb-4">
                  <i className="ri-inbox-line text-4xl sm:6xl w-12 h-12 sm:w-16 sm:h-16 flex items-center justify-center mx-auto"></i>
                </div>
                <h3 className="text-base sm:lg:text-lg font-semibold text-gray-700 mb-2">
                  Keine Produkte vorhanden
                </h3>
                <p className="text-gray-500 text-sm sm:text-base">
                  Fügen Sie Ihr erstes Produkt hinzu, um zu beginnen.
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-6">
                {filteredProducts.map((product) => {
                  const stockStatus = getStockStatus(product);
                  const expiryStatus = getExpiryStatus(product.expiryDate);
                  const hasSpecific = hasSpecificPrices(product);
                  
                  return (
                    <div key={product.id} className="bg-white rounded-lg shadow-sm border overflow-hidden hover:shadow-md transition-shadow">
                      <div className="p-3 sm:p-6">
                        <div className="flex items-start justify-between mb-3 sm:mb-4">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center space-x-2">
                              <h3 className="text-sm sm:text-lg font-semibold text-gray-900 truncate">{product.name}</h3>
                              {hasSpecific && (
                                <div className="flex-shrink-0">
                                  <span 
                                    className="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800"
                                    title="Dieses Produkt hat automatspezifische Preise"
                                  >
                                    <i className="ri-price-tag-3-line mr-1"></i>
                                    Spez. Preise
                                  </span>
                                </div>
                              )}
                            </div>
                            <p className="text-xs sm:text-sm text-gray-500">{product.category}</p>
                          </div>
                          <div className="flex flex-col items-end space-y-1">
                            <span className={`px-1.5 sm:px-2 py-0.5 sm:py-1 text-xs font-medium rounded-full ${stockStatus.color}`}>
                              <i className={`${stockStatus.icon} mr-1`}></i>
                              {product.currentStock}/{product.minStock}
                            </span>
                            {expiryStatus && (
                              <span className={`px-1.5 sm:px-2 py-0.5 sm:py-1 text-xs font-medium rounded-full ${expiryStatus.color}`}>
                                <i className="ri-calendar-line mr-1"></i>
                                {expiryStatus.text}
                              </span>
                            )}
                          </div>
                        </div>
                        
                        <div className="space-y-1.5 sm:space-y-2 mb-3 sm:mb-4">
                          <div className="flex items-center justify-between text-xs sm:text-sm">
                            <span className="text-gray-600">Standort:</span>
                            <span className="font-medium">{product.location}</span>
                          </div>
                          <div className="flex items-center justify-between text-xs sm:text-sm">
                            <span className="text-gray-600">Lieferanten:</span>
                            <span className="font-medium">{product.supplier}</span>
                          </div>
                          <div className="flex items-center justify-between text-xs sm:text-sm">
                            <span className="text-gray-600">Einkaufspreis:</span>
                            <span className="font-medium">CHF{product.purchasePrice.toFixed(2)}</span>
                          </div>
                          <div className="flex items-center justify-between text-xs sm:text-sm">
                            <span className="text-gray-600">Standard-Verkaufspreis:</span>
                            <span className="font-medium text-green-600">CHF{product.sellPrice.toFixed(2)}</span>
                          </div>
                          
                          {hasSpecific && (
                            <div className="mt-2 pt-2 border-t border-gray-100">
                              <div className="text-xs text-orange-600 font-medium mb-1 flex items-center">
                                <i className="ri-robot-line mr-1"></i>
                                Automatspezifische Preise:
                              </div>
                              <div className="space-y-1">
                                {Object.entries(product.automatPrices || {}).map(([automatId, price]) => {
                                  const automat = automaten.find(a => a.id === automatId);
                                  return (
                                    <div key={automatId} className="flex items-center justify-between text-xs">
                                      <span className="text-gray-600 truncate">
                                        {automat?.name || 'Unbekannter Automat'}:
                                      </span>
                                      <span className="font-medium text-orange-600 ml-2">
                                        CHF{parseFloat(price.toString()).toFixed(2)}
                                      </span>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          )}
                          
                          {product.divisible && (
                            <div className="flex items-center text-xs sm:text-sm text-blue-600">
                              <i className="ri-scissors-line mr-1"></i>
                              Teilbar
                            </div>
                          )}
                        </div>
                        
                        {/* Bestandsstatus */}
                        <div className="pt-3 sm:pt-4 border-t border-gray-100">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-xs sm:text-sm text-gray-600">Bestandsverlauf</span>
                            <span className="text-xs text-gray-400">Status</span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-1.5 sm:h-2">
                            <div 
                              className={`h-1.5 sm:h-2 rounded-full ${
                                product.currentStock <= product.minStock ? 'bg-red-500' :
                                product.currentStock <= product.minStock * 1.5 ? 'bg-yellow-500' : 'bg-green-500'
                              }`}
                              style={{ width: `${Math.min(100, (product.currentStock / (product.minStock * 2)) * 100)}%` }}
                            ></div>
                          </div>
                        </div>
                        
                        <div className="mt-3 sm:mt-4 flex space-x-2">
                          <Button 
                            size="sm" 
                            variant="secondary" 
                            className="flex-1 text-xs"
                            onClick={() => handleEdit(product)}
                          >
                            <i className="ri-edit-line mr-1"></i>
                            Bearbeiten
                          </Button>
                          <div className="relative more-menu-container">
                            <Button 
                              size="sm" 
                              variant="secondary" 
                              className="text-xs px-2 sm:px-3"
                              onClick={() => setShowMoreMenu(showMoreMenu === product.id ? null : product.id)}
                            >
                              <i className="ri-more-line"></i>
                            </Button>
                            
                            {showMoreMenu === product.id && (
                              <div className="absolute right-0 bottom-full mb-1 w-48 bg-white rounded-md shadow-lg border z-50">
                                <div className="py-1">
                                  <button
                                    onClick={() => handleDuplicate(product)}
                                    className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 cursor-pointer"
                                  >
                                    <i className="ri-file-copy-line mr-2"></i>
                                    Duplizieren
                                  </button>
                                  <button
                                    onClick={() => handleEdit(product)}
                                    className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 cursor-pointer"
                                  >
                                    <i className="ri-edit-line mr-2"></i>
                                    Details bearbeiten
                                  </button>
                                  <div className="border-t border-gray-100"></div>
                                  <button
                                    onClick={() => handleDelete(product)}
                                    className="flex items-center w-full px-4 py-2 text-sm text-red-600 hover:bg-red-50 cursor-pointer"
                                  >
                                    <i className="ri-delete-bin-line mr-2"></i>
                                    Löschen
                                  </button>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Modal für neues Produkt */}
      <Modal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          resetForm();
        }}
        title="Neues Produkt hinzufügen"
        maxWidth="xl"
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Produktname"
              value={formData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              placeholder="z.B. Coca Cola 0,5L"
              required
            />
            
            <Select
              label="Kategorie"
              options={categories.map(cat => ({ value: cat, label: cat }))}
              value={formData.category}
              onChange={(value) => handleInputChange('category', value)}
              required
            />
            
            <Input
              label="Einkaufspreis (CHF)"
              type="number"
              step="0.01"
              value={formData.purchasePrice}
              onChange={(e) => handleInputChange('purchasePrice', e.target.value)}
              placeholder="0.00"
              required
            />
            
            <Input
              label="Standard-Verkaufspreis (CHF)"
              type="number" 
              step="0.01"
              value={formData.sellPrice}
              onChange={(e) => handleInputChange('sellPrice', e.target.value)}
              placeholder="0.00"
              required
            />
            
            <Input
              label="Standort"
              value={formData.location}
              onChange={(e) => handleInputChange('location', e.target.value)}
              placeholder="z.B. Lager A oder Automat 1"
              required
            />
            
            <Input
              label="Lieferant"
              value={formData.supplier}
              onChange={(e) => handleInputChange('supplier', e.target.value)}
              placeholder="z.B. Getränke GmbH"
              required
            />
            
            <Input
              label="Mindestbestand"
              type="number"
              value={formData.minStock}
              onChange={(e) => handleInputChange('minStock', e.target.value)}
              placeholder="10"
              required
            />
            
            <Input
              label="Aktueller Bestand"
              type="number"
              value={formData.currentStock}
              onChange={(e) => handleInputChange('currentStock', e.target.value)}
              placeholder="50"
              required
            />
          </div>
          
          <Input
            label="Ablaufdatum"
            type="date"
            value={formData.expiryDate}
            onChange={(e) => handleInputChange('expiryDate', e.target.value)}
          />
          
          <div className="flex items-center">
            <input
              type="checkbox"
              id="divisible"
              checked={formData.divisible}
              onChange={(e) => handleInputChange('divisible', e.target.checked)}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
            <label htmlFor="divisible" className="ml-2 block text-sm text-gray-900">
              Produkt ist teilbar (einzelne Einheiten verkaufbar)
            </label>
          </div>

          {/* Automatspezifische Preise */}
          {automaten.length > 0 && (
            <div className="space-y-4">
              <div className="border-t border-gray-200 pt-4">
                <div className="flex items-center space-x-2 mb-3">
                  <i className="ri-robot-line text-orange-600"></i>
                  <h4 className="text-sm font-medium text-gray-900">Automatspezifische Preise (optional)</h4>
                </div>
                <p className="text-xs text-gray-600 mb-4">
                  Lassen Sie die Felder leer, um den Standard-Verkaufspreis zu verwenden. 
                  Geben Sie nur bei abweichenden Preisen einen Wert ein.
                </p>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {automaten.map((automat) => (
                    <div key={automat.id} className="bg-gray-50 p-3 rounded-lg border">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        <i className="ri-robot-line mr-1 text-gray-600"></i>
                        {automat.name}
                      </label>
                      <Input
                        type="number"
                        step="0.01"
                        value={formData.automatPrices[automat.id] || ''}
                        onChange={(e) => handleAutomatPriceChange(automat.id, e.target.value)}
                        placeholder={`Standard: CHF ${formData.sellPrice || '0.00'}`}
                        className="text-sm"
                      />
                      {formData.automatPrices[automat.id] && (
                        <p className="text-xs text-orange-600 mt-1 flex items-center">
                          <i className="ri-price-tag-3-line mr-1"></i>
                          Spezifischer Preis: CHF {parseFloat(formData.automatPrices[automat.id]).toFixed(2)}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
          
          <div className="flex space-x-3 pt-4">
            <Button type="submit" className="flex-1">
              Produkt hinzufügen
            </Button>
            <Button 
              type="button" 
              variant="secondary" 
              onClick={() => {
                setIsModalOpen(false);
                resetForm();
              }}
            >
              Abbrechen
            </Button>
          </div>
        </form>
      </Modal>

      {/* Modal für Produkt bearbeiten */}
      <Modal
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
          setEditingProduct(null);
          resetForm();
        }}
        title="Produkt bearbeiten"
        maxWidth="xl"
      >
        <form onSubmit={handleEditSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Produktname"
              value={formData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              placeholder="z.B. Coca Cola 0,5L"
              required
            />
            
            <Select
              label="Kategorie"
              options={categories.map(cat => ({ value: cat, label: cat }))}
              value={formData.category}
              onChange={(value) => handleInputChange('category', value)}
              required
            />
            
            <Input
              label="Einkaufspreis (CHF)"
              type="number"
              step="0.01"
              value={formData.purchasePrice}
              onChange={(e) => handleInputChange('purchasePrice', e.target.value)}
              placeholder="0.00"
              required
            />
            
            <Input
              label="Standard-Verkaufspreis (CHF)"
              type="number" 
              step="0.01"
              value={formData.sellPrice}
              onChange={(e) => handleInputChange('sellPrice', e.target.value)}
              placeholder="0.00"
              required
            />
            
            <Input
              label="Standort"
              value={formData.location}
              onChange={(e) => handleInputChange('location', e.target.value)}
              placeholder="z.B. Lager A oder Automat 1"
              required
            />
            
            <Input
              label="Lieferant"
              value={formData.supplier}
              onChange={(e) => handleInputChange('supplier', e.target.value)}
              placeholder="z.B. Getränke GmbH"
              required
            />
            
            <Input
              label="Mindestbestand"
              type="number"
              value={formData.minStock}
              onChange={(e) => handleInputChange('minStock', e.target.value)}
              placeholder="10"
              required
            />
            
            <Input
              label="Aktueller Bestand"
              type="number"
              value={formData.currentStock}
              onChange={(e) => handleInputChange('currentStock', e.target.value)}
              placeholder="50"
              required
            />
          </div>
          
          <Input
            label="Ablaufdatum"
            type="date"
            value={formData.expiryDate}
            onChange={(e) => handleInputChange('expiryDate', e.target.value)}
          />
          
          <div className="flex items-center">
            <input
              type="checkbox"
              id="divisible-edit"
              checked={formData.divisible}
              onChange={(e) => handleInputChange('divisible', e.target.checked)}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
            <label htmlFor="divisible-edit" className="ml-2 block text-sm text-gray-900">
              Produkt ist teilbar (einzelne Einheiten verkaufbar)
            </label>
          </div>

          {/* Automatspezifische Preise bearbeiten */}
          {automaten.length > 0 && (
            <div className="space-y-4">
              <div className="border-t border-gray-200 pt-4">
                <div className="flex items-center space-x-2 mb-3">
                  <i className="ri-robot-line text-orange-600"></i>
                  <h4 className="text-sm font-medium text-gray-900">Automatspezifische Preise bearbeiten</h4>
                </div>
                <p className="text-xs text-gray-600 mb-4">
                  Lassen Sie die Felder leer, um den Standard-Verkaufspreis zu verwenden. 
                  Geben Sie nur bei abweichenden Preisen einen Wert ein.
                </p>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {automaten.map((automat) => (
                    <div key={automat.id} className="bg-gray-50 p-3 rounded-lg border">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        <i className="ri-robot-line mr-1 text-gray-600"></i>
                        {automat.name}
                      </label>
                      <Input
                        type="number"
                        step="0.01"
                        value={formData.automatPrices[automat.id] || ''}
                        onChange={(e) => handleAutomatPriceChange(automat.id, e.target.value)}
                        placeholder={`Standard: CHF ${formData.sellPrice || '0.00'}`}
                        className="text-sm"
                      />
                      {formData.automatPrices[automat.id] && (
                        <p className="text-xs text-orange-600 mt-1 flex items-center">
                          <i className="ri-price-tag-3-line mr-1"></i>
                          Spezifischer Preis: CHF {parseFloat(formData.automatPrices[automat.id]).toFixed(2)}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
          
          <div className="flex space-x-3 pt-4">
            <Button type="submit" className="flex-1">
              Änderungen speichern
            </Button>
            <Button 
              type="button" 
              variant="secondary"
              onClick={() => {
                setIsEditModalOpen(false);
                setEditingProduct(null);
                resetForm();
              }}
            >
              Abbrechen
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
